import { useState } from 'react';
import { discussionPosts } from '@/mocks/home';
import SectionHeading from './SectionHeading';

export default function Discussion() {
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const [likedIds, setLikedIds] = useState<string[]>([]);

  const toggleLike = (id: string) => {
    setLikedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleDraftAction = () => {
    setNotice(
      draft.trim()
        ? '这段想法已经记在本地草稿里，正式发布通道还在排队，先谢谢你的耐心。'
        : '先写两句吧，评论区的位置一直给你留着。',
    );
  };

  return (
    <section id="discussion" className="w-full scroll-mt-24 bg-background-100 py-16 md:py-24 md:scroll-mt-28">
      <div className="mx-auto w-full max-w-shell px-4 md:px-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            kicker="04 / DISCUSSION"
            title="争议事件 · 社区讨论"
            desc="争议从来不是一句话能讲完的。这里收着读者最常被转发的几条讨论，观点各自保留，不作裁决。"
          />
          <div
            data-reveal
            className="flex flex-none items-center gap-2 self-start rounded-full border border-background-300 bg-background-50 px-4 py-2 md:self-auto"
          >
            <i className="ri-chat-3-line text-sm text-foreground-600" />
            <span className="text-xs text-foreground-600">9,842 人正在讨论</span>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:mt-14 md:grid-cols-2">
          {discussionPosts.map((post) => {
            const liked = likedIds.includes(post.id);
            return (
              <article
                key={post.id}
                data-reveal
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-secondary-500 font-heading text-lg font-bold text-background-50">
                    {post.initial}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground-900">{post.name}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">{post.time}</p>
                  </div>
                  <span className="ml-auto flex-none rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800">
                    {post.tag}
                  </span>
                </div>

                <p className="mt-4 flex-1 text-sm leading-7 text-foreground-700">{post.content}</p>

                <div className="mt-5 flex items-center gap-4 border-t border-background-200 pt-4">
                  <button
                    type="button"
                    onClick={() => toggleLike(post.id)}
                    className={`flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${
                      liked ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                    }`}
                  >
                    <i className={liked ? 'ri-heart-3-fill text-base' : 'ri-heart-3-line text-base'} />
                    {post.likes + (liked ? 1 : 0)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNotice('回复功能还在整理中，先记下你的兴趣。')}
                    className="flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-900 cursor-pointer"
                  >
                    <i className="ri-chat-1-line text-base" />
                    {post.replies}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNotice('已复制这条讨论的链接，可以分享给朋友。')}
                    className="ml-auto flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-900 cursor-pointer"
                  >
                    <i className="ri-share-forward-line text-base" />
                    分享
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {/* 讨论输入区（静态展示） */}
        <div data-reveal className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-heading text-base font-bold text-foreground-950">说说你的看法</p>
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-lock-2-line text-sm" />
              仅展示，暂不开放发布
            </span>
          </div>
          <textarea
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value.slice(0, 200));
              setNotice('');
            }}
            rows={3}
            placeholder="你注意到过哪次翻车？写下来，也许会成为下一份档案的一部分。"
            className="mt-4 w-full resize-none rounded-md border border-background-300 bg-background-50 p-4 text-sm leading-7 text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-foreground-500">{draft.length} / 200</span>
            <div className="flex items-center gap-3">
              {notice && <span className="text-xs text-primary-600">{notice}</span>}
              <button
                type="button"
                onClick={handleDraftAction}
                className="inline-flex items-center gap-2 rounded-md bg-foreground-950 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line text-base" />
                提交想法
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}